import { Fragment } from 'react';

import { appBorders, appColors, SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme, useMediaQuery } from '@mui/material';
import Xarrow from 'react-xarrows';

import { IntroSlide } from '../hooks/IntroSlidesPage.hooks';

const INTRO_STRINGS = appStrings.modules.intro;

type IntroSlideContentProps = Readonly<{
  slide: IntroSlide;
  slidesLength: number;
  index: number;
  currentSlide: number;
  setCurrentSlide: (slide: number) => void;
}>;

export const IntroSlideContent = ({
  slide,
  index,
  slidesLength,
  currentSlide,
  setCurrentSlide,
}: IntroSlideContentProps) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const mobileStartAnchor = index % 2 ? 'bottom' : 'top';

  const getSlideMarginLeft = () => {
    if (index === 0) {
      return isMobile ? '0' : `${SIDEBAR_COLLAPSED_WIDTH}px`;
    }

    if (index === slidesLength - 1) {
      return 0;
    }

    return `${isMobile ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_COLLAPSED_WIDTH * 2}px`;
  };

  return (
    <Fragment key={index}>
      {/* STANDARD SLIDE AND SLIDE CONTENT */}
      {slide.variant.type === 'STANDARD' && (
        <Box
          sx={{
            ...slideSx,
            minWidth: { xs: index === 0 ? '70vw' : '50vw', sm: '75vw' },
            mt: index % 2 === 0 ? '10vh' : '0',
            ml: getSlideMarginLeft(),
            mr: index === slidesLength - 2 ? '160px' : '0',
          }}
          data-scroll-id={`slide-${index}`}
        >
          <Box
            id={`slide-${index}`}
            sx={{
              ...slideContentSx,
              top: index % 2 === 1 ? (isMobile ? '45vh' : '35vh') : '0',
              cursor: index === currentSlide ? 'default' : 'pointer',
            }}
            onClick={() => {
              setCurrentSlide(index);
            }}
          >
            <Box sx={factsHeaderContainerSx}>
              <Typography sx={factsWordSx}>{INTRO_STRINGS.facts}</Typography>
              <Box
                component="img"
                src={slide.variant.imagePath}
                alt="slide"
                sx={factsImageSx}
                draggable={false}
              />
            </Box>

            <Typography sx={factsBodySx}>
              <Typography component="span" sx={factsTitleSx}>
                {slide.variant.title}
              </Typography>
              {slide.variant.description}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ELEMENT SLIDE AND SLIDE CONTENT */}
      {slide.variant.type === 'ELEMENT' &&
        slide.variant.element({ index, currentSlide, setCurrentSlide })}

      {/* ARROWS FOR CONNECTING SLIDES */}
      {index !== slidesLength - 1 && (
        <Xarrow
          start={`slide-${index}`}
          end={`slide-${index + 1}`}
          color={appColors.color.blue1}
          path="grid"
          strokeWidth={10}
          showHead={false}
          key={`xarrow-${currentSlide}`}
          zIndex={-1}
          startAnchor={isMobile ? mobileStartAnchor : slide.startAnchor}
          endAnchor={isMobile ? 'left' : slide.endAnchor}
        />
      )}
    </Fragment>
  );
};

const slideSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  minWidth: { xs: '50vw', sm: '75vw' },
  width: { sm: '100%' },
  position: 'relative',
} as const;

const slideContentSx: SxProps<Theme> = {
  cursor: 'pointer',
  boxSizing: 'border-box',
  position: 'relative',
  ...appBorders.backrsDropShadowBorders.black,
  borderRadius: '8px',
  padding: {
    xs: '16px',
    sm: '36px',
  },
  maxWidth: '600px',
  whiteSpace: 'pre-wrap',
  backgroundColor: appColors.neutral.white,
  flexWrap: 'wrap',
  ml: {
    xs: '16px',
    sm: '0',
  },
} as const;

const factsHeaderContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  boxSizing: 'border-box',
} as const;

const factsWordSx: SxProps<Theme> = {
  fontSize: {
    xs: '16px',
    sm: '32px',
  },
  textAlign: {
    xs: 'center',
    sm: 'left',
  },
  fontWeight: 800,
  color: appColors.color.blue1,
} as const;

const factsImageSx: SxProps<Theme> = {
  height: 'clamp(64px, 10vw, 82px)',
  width: 'auto',
  alignSelf: 'flex-start',
  ml: {
    xs: 0,
    sm: 3,
  },
  maxWidth: {
    xs: '70%',
    sm: 'unset',
  },
} as const;

const factsBodySx: SxProps<Theme> = {
  fontSize: {
    xs: '14pt',
    sm: '24px',
    md: '40px',
  },
  '@media (max-height: 768px)': {
    fontSize: '36px',
    mt: 2,
  },
  '@media (max-height: 600px)': {
    fontSize: '20px',
    mt: 1,
  },
  '@media (max-width: 500px)': {
    fontSize: '20px',
    mt: 1,
  },
  mt: {
    xs: 1,
    sm: 3,
  },
  lineHeight: 1.3,
} as const;

const factsTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: {
    xs: '14pt',
    sm: '24px',
    md: '40px',
  },
  '@media (max-height: 800px)': {
    fontSize: '36px',
  },
  '@media (max-height: 600px)': {
    fontSize: '20px',
  },
  '@media (max-width: 500px)': {
    fontSize: '20px',
  },
} as const;
